'use client';
import Image from 'next/image';
import { useState } from 'react';

/* Image Gallery Component */
interface ImagePlaceholder {
  src: string;
  base64: string;
}

interface Product {
  partNumber: string;
}

const ImageGallery = ({
  plaiceholders,
  product,
}: {
  plaiceholders: ImagePlaceholder[];
  product: Product;
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedImage = plaiceholders[selectedIndex];

  const [zoomStyle, setZoomStyle] = useState<{ transform: string; transformOrigin?: string }>({ transform: 'scale(1)' });

  // Navigate to previous image
  const prevImage = () => {
    setSelectedIndex((prevIndex) =>
      prevIndex === 0 ? plaiceholders.length - 1 : prevIndex - 1,
    );
  };

  // Navigate to next image
  const nextImage = () => {
    setSelectedIndex((prevIndex) =>
      prevIndex === plaiceholders.length - 1 ? 0 : prevIndex + 1,
    );
  };

  // Handle zoom effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transform: 'scale(2)',
      transformOrigin: `${x}% ${y}%`,
    });
  };

  const resetZoom = () => {
    setZoomStyle({ transform: 'scale(1)' });
  };

  return (
    <div className='mx-auto w-full max-w-lg'>
      {/* Main Image with Navigation Buttons */}
      <div className='relative mb-4 flex aspect-square items-center justify-center lg:mb-8'>
        {/* Left Navigation Button (Moved Outside Image Area) */}
        <button
          onClick={prevImage}
          className='absolute left-[-50px] top-1/2 -translate-y-1/2 z-10 rounded-full bg-white bg-opacity-70 p-3 shadow-md transition hover:bg-opacity-90'
        >
          ◀
        </button>

        {/* Image Container with Zoom Effect */}
        <div
          className="relative w-full h-full overflow-hidden rounded-lg p-2"
          onMouseMove={handleMouseMove}
          onMouseLeave={resetZoom}
        >
          <Image
            src={selectedImage.src}
            alt={product.partNumber}
            placeholder='blur'
            blurDataURL={selectedImage.base64}
            layout='fill'
            objectFit='contain'
            className='rounded-lg transition-transform duration-300 ease-in-out'
            style={zoomStyle}
          />
        </div>

        {/* Right Navigation Button (Moved Outside Image Area) */}
        <button
          onClick={nextImage}
          className='absolute right-[-50px] top-1/2 -translate-y-1/2 z-10 rounded-full bg-white bg-opacity-70 p-3 shadow-md transition hover:bg-opacity-90'
        >
          ▶
        </button>
      </div>

      {/* Thumbnails */}
      <div className='flex gap-2 overflow-x-auto'>
        {plaiceholders.map((img, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className={`h-16 w-16 overflow-hidden rounded-lg border-2 ${
              selectedIndex === index ? 'border-blue-500' : 'border-gray-200'
            }`}
          >
            <Image
              src={img.src}
              alt={product.partNumber}
              placeholder='blur'
              blurDataURL={img.base64}
              width={64}
              height={64}
              className='object-cover'
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;
